import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { 
  FiSend, 
  FiImage, 
  FiUsers, 
  FiClock, 
  FiGift, 
  FiSettings, 
  FiCalendar, 
  FiCheckCircle, 
  FiX,
  FiAlertCircle,
  FiUpload,
  FiPlus,
  FiTrash2,
  FiCopy,
  FiXCircle,
  FiEye,
  FiChevronDown,
  FiChevronUp
} from 'react-icons/fi';

function RestaurantBroadcast() {
  const { alert, confirm } = useModal();
  const [showAutomationTab, setShowAutomationTab] = useState(false);
  
  // Manual Broadcast state
  const [broadcastType, setBroadcastType] = useState('marketing');
  const [targetType, setTargetType] = useState('all');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [availableGuests, setAvailableGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(true);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const fileInputRef = useRef(null);
  
  // Birthday/Anniversary Automation state
  const [automationSettings, setAutomationSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [sentHistory, setSentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState([]);
  const [loadingQueued, setLoadingQueued] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [newDiscount, setNewDiscount] = useState('');

  useEffect(() => {
    fetchBroadcasts();
    if (showAutomationTab) {
      fetchAutomationSettings();
    }
  }, [showAutomationTab]);

  const fetchAutomationSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await api.get('/api/broadcast/automation/settings');
      setAutomationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch automation settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      setLoadingEvents(true);
      const response = await api.get('/api/broadcast/automation/upcoming?daysBefore=7');
      console.log('Upcoming events response:', response.data);
      setUpcomingEvents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch upcoming events:', error);
      setUpcomingEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchSentHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/api/broadcast/automation/history?limit=20');
      setSentHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch sent history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchQueuedMessages = async () => {
    try {
      setLoadingQueued(true);
      const response = await api.get('/api/broadcast/automation/queued');
      setQueuedMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch queued messages:', error);
    } finally {
      setLoadingQueued(false);
    }
  };

  const updateDiscount = async (queueId, currentDiscount) => {
    if (!newDiscount || newDiscount < 5 || newDiscount > 25) {
      await alert('Discount must be between 5 and 25%', 'Validation Error', 'warning');
      return;
    }

    try {
      await api.put(`/api/broadcast/automation/queued/${queueId}/discount`, {
        discount_percentage: parseInt(newDiscount)
      });
      await alert('Discount updated successfully!', 'Success', 'success');
      setEditingDiscount(null);
      setNewDiscount('');
      fetchQueuedMessages();
    } catch (error) {
      console.error('Failed to update discount:', error);
      await alert('Failed to update discount', 'Error', 'error');
    }
  };

  const queueEvents = async () => {
    const confirmed = await confirm('Queue upcoming birthday/anniversary events?', 'Queue Events', 'info');
    if (!confirmed) return;
    try {
      setSavingSettings(true);
      const response = await api.post('/api/broadcast/automation/queue');
      await alert(`Queued ${response.data.queued} events. ${response.data.skipped} skipped.`, 'Success', 'success');
      fetchQueuedMessages();
    } catch (error) {
      console.error('Failed to queue events:', error);
      await alert('Failed to queue events', 'Error', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const processQueued = async () => {
    const confirmed = await confirm('Process and send queued birthday/anniversary messages now?', 'Process Messages', 'info');
    if (!confirmed) return;
    try {
      setSavingSettings(true);
      const response = await api.post('/api/broadcast/automation/process-queued');
      await alert(`Processed ${response.data.processed} messages. ${response.data.sent} sent, ${response.data.failed} failed.`, 'Success', 'success');
      fetchQueuedMessages();
      fetchSentHistory();
      fetchBroadcasts();
    } catch (error) {
      console.error('Failed to process queued messages:', error);
      await alert('Failed to process queued messages', 'Error', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const cancelQueued = async (queueId) => {
    const confirmed = await confirm('Cancel this queued message?', 'Cancel Message', 'warning');
    if (!confirmed) return;
    try {
      await api.delete(`/api/broadcast/automation/queued/${queueId}`);
      await alert('Queued message cancelled', 'Success', 'success');
      fetchQueuedMessages();
    } catch (error) {
      console.error('Failed to cancel queued message:', error);
      await alert('Failed to cancel queued message', 'Error', 'error');
    }
  };

  const saveAutomationSettings = async (e) => {
    e.preventDefault();
    try {
      setSavingSettings(true);
      await api.put('/api/broadcast/automation/settings', automationSettings);
      await alert('Settings saved successfully!', 'Success', 'success');
      fetchUpcomingEvents();
    } catch (error) {
      console.error('Failed to save settings:', error);
      await alert('Failed to save settings', 'Error', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const processEventsNow = async () => {
    const confirmed = await confirm('Process and send birthday/anniversary messages now?', 'Process Events', 'info');
    if (!confirmed) return;
    try {
      setSavingSettings(true);
      const response = await api.post('/api/broadcast/automation/process');
      await alert(`Processed ${response.data.processed} events. ${response.data.sent} sent, ${response.data.failed} failed.`, 'Success', 'success');
      fetchUpcomingEvents();
      fetchSentHistory();
      fetchBroadcasts();
    } catch (error) {
      console.error('Failed to process events:', error);
      await alert('Failed to process events', 'Error', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    if (targetType === 'phone_based') {
      loadAvailableGuests();
    } else {
      setAvailableGuests([]);
      setSelectedGuests([]);
    }
  }, [targetType]);

  const fetchBroadcasts = async () => {
    try {
      setLoadingBroadcasts(true);
      const response = await api.get('/api/broadcast/restaurant');
      setBroadcasts(response.data.broadcasts || []);
    } catch (error) {
      console.error('Failed to fetch broadcasts:', error);
    } finally {
      setLoadingBroadcasts(false);
    }
  };

  const loadAvailableGuests = async () => {
    try {
      setLoadingGuests(true);
      const response = await api.get('/api/guests/restaurant');
      setAvailableGuests(response.data || []);
    } catch (error) {
      console.error('Failed to load guests:', error);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        await alert('Please select an image file', 'Image Required', 'warning');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        await alert('Image size must be less than 5MB', 'File Too Large', 'warning');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      uploadImage(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/api/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const publicUrl = response.data?.url;
      if (!publicUrl) {
        throw new Error('Upload succeeded but no public URL returned');
      }

      setImageUrl(publicUrl);
      setImagePreview(publicUrl);
    } catch (error) {
      console.error('Failed to upload image:', error);
      await alert('Failed to upload image. Please try again.', 'Upload Failed', 'error');
    }
  };

  const handleGuestToggle = (guestPhone) => {
    setSelectedGuests(prev => {
      if (prev.includes(guestPhone)) {
        return prev.filter(phone => phone !== guestPhone);
      } else {
        return [...prev, guestPhone];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedGuests.length === availableGuests.length) {
      setSelectedGuests([]);
    } else {
      setSelectedGuests(availableGuests.map(g => g.phone));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !message) {
      await alert('Please fill in title and message', 'Validation Error', 'warning');
      return;
    }

    if (!imageUrl) {
      await alert('Image is required for WhatsApp template broadcasts (marketing_offer / event_announcements).', 'Image Required', 'warning');
      return;
    }

    if (!/^https?:\/\//i.test(imageUrl)) {
      await alert('Please provide a public HTTPS image URL. WhatsApp templates do not accept local or data URLs.', 'Invalid Image URL', 'warning');
      return;
    }

    if (targetType === 'phone_based' && selectedGuests.length === 0) {
      await alert('Please select at least one guest', 'Guest Required', 'warning');
      return;
    }

    try {
      setSending(true);
      
      // Step 1: Create the broadcast
      const response = await api.post('/api/broadcast/restaurant', {
        title,
        message,
        image_url: imageUrl || null,
        broadcastType,
        targetAudience: targetType,
        phoneNumbers: targetType === 'phone_based' ? selectedGuests : null
      });

      const broadcastId = response.data.broadcast.id;

      // Step 2: Send the broadcast immediately
      const sendResponse = await api.post(`/api/broadcast/restaurant/${broadcastId}/send`);
      
      await alert(
        `Broadcast sent successfully! ${sendResponse.data.targetCount} messages queued for delivery.`, 
        'Success', 
        'success'
      );
      
      // Reset form
      setTitle('');
      setMessage('');
      setImagePreview(null);
      setImageUrl('');
      setSelectedGuests([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      fetchBroadcasts();
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to send broadcast';
      await alert(errorMsg, 'Error', 'error');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Check form completion status
  const isStep1Complete = title && message;
  const isStep2Complete = targetType === 'all' || targetType === 'regular_customers' || (targetType === 'phone_based' && selectedGuests.length > 0);
  const canSubmit = isStep1Complete && isStep2Complete;

  return (
    <Layout title="Broadcast Messages">
      <div className="space-y-6">
        {/* Header Section */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Broadcast Messages</h1>
          <p className="text-sm text-gray-600">Send messages to guests and automate birthday/anniversary offers</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-3 border-b-2 border-gray-200">
          <button
            onClick={() => setShowAutomationTab(false)}
            className={`px-6 py-3 font-semibold text-base transition-all relative ${
              !showAutomationTab
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FiSend className="inline mr-2" />
            Manual Broadcasts
          </button>
          <button
            onClick={() => {
              setShowAutomationTab(true);
              fetchAutomationSettings();
              fetchUpcomingEvents();
              fetchSentHistory();
              fetchQueuedMessages();
            }}
            className={`px-6 py-3 font-semibold text-base transition-all relative ${
              showAutomationTab
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FiGift className="inline mr-2" />
            Birthday/Anniversary Automation
          </button>
        </div>

        {showAutomationTab ? (
          <div className="space-y-6">
            {/* Automation Settings */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Automation Settings</h2>
                <p className="text-sm text-gray-500">Configure automatic birthday and anniversary messages</p>
              </div>
              
              {loadingSettings ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading settings...</p>
                </div>
              ) : automationSettings ? (
                <form onSubmit={saveAutomationSettings} className="space-y-4">
                  <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      id="isEnabled"
                      checked={automationSettings.is_enabled}
                      onChange={(e) => setAutomationSettings({...automationSettings, is_enabled: e.target.checked})}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="isEnabled" className="text-sm font-medium text-gray-900 cursor-pointer">
                      Enable automatic birthday/anniversary messages
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Send Days Before Event
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="7"
                        value={automationSettings.send_days_before || 3}
                        onChange={(e) => setAutomationSettings({...automationSettings, send_days_before: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">Send message 3-4 days before birthday/anniversary</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Percentage (%)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="25"
                        value={automationSettings.discount_percentage || 15}
                        onChange={(e) => setAutomationSettings({...automationSettings, discount_percentage: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">10-15% recommended for best results</p>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap pt-1">
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50 font-medium text-sm transition-colors"
                    >
                      {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                    <button
                      type="button"
                      onClick={queueEvents}
                      disabled={savingSettings || !automationSettings.is_enabled}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium text-sm transition-colors"
                    >
                      Queue Events
                    </button>
                    <button
                      type="button"
                      onClick={processQueued}
                      disabled={savingSettings || !automationSettings.is_enabled}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm transition-colors"
                    >
                      Process Queued
                    </button>
                    <button
                      type="button"
                      onClick={processEventsNow}
                      disabled={savingSettings || !automationSettings.is_enabled}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium text-sm transition-colors"
                    >
                      Queue & Send Now
                    </button>
                  </div>
                </form>
              ) : null}
            </div>

            {/* Upcoming Events */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-0.5">Upcoming Events</h2>
                  <p className="text-sm text-gray-500">Next 7 days</p>
                </div>
                <button
                  onClick={fetchUpcomingEvents}
                  disabled={loadingEvents}
                  className="bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium text-xs transition-colors"
                >
                  Refresh
                </button>
              </div>
              
              {loadingEvents ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading events...</p>
                </div>
              ) : upcomingEvents.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <FiCalendar className="text-3xl text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-medium">No upcoming birthdays or anniversaries</p>
                  <p className="text-xs text-gray-500 mt-1">Events will appear here when guests have upcoming celebrations</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {upcomingEvents.map((event, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                      <div className="flex items-start gap-3">
                        <div className={`rounded-xl p-3 flex-shrink-0 ${
                          event.eventType === 'birthday' 
                            ? 'bg-gradient-to-br from-pink-100 to-pink-200' 
                            : 'bg-gradient-to-br from-purple-100 to-purple-200'
                        }`}>
                          <span className="text-2xl">
                            {event.eventType === 'birthday' ? '🎂' : '💑'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              event.eventType === 'birthday' 
                                ? 'bg-pink-100 text-pink-800 border border-pink-200' 
                                : 'bg-purple-100 text-purple-800 border border-purple-200'
                            }`}>
                              {event.eventType === 'birthday' ? 'Birthday' : 'Anniversary'}
                            </span>
                          </div>
                          <h3 className="font-bold text-gray-900 text-lg mb-1">{event.guest.name}</h3>
                          <p className="text-sm text-gray-600 font-medium mb-2">
                            {formatDate(event.eventDate)}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className="bg-gray-100 rounded-full px-3 py-1">
                              <p className="text-xs font-semibold text-gray-700">
                                {event.daysUntil} day{event.daysUntil !== 1 ? 's' : ''} until event
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Queued Messages */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Queued Messages</h2>
                <p className="text-sm text-gray-500">Messages waiting to be sent</p>
              </div>
              
              {loadingQueued ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading queued messages...</p>
                </div>
              ) : queuedMessages.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <FiClock className="text-3xl text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-medium">No queued messages</p>
                  <p className="text-xs text-gray-500 mt-1">Click "Queue Events" to find and queue upcoming birthdays/anniversaries</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {queuedMessages.map((queued) => (
                    <div key={queued.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`rounded-xl p-2 ${
                              queued.event_type === 'birthday' 
                                ? 'bg-gradient-to-br from-pink-100 to-pink-200' 
                                : 'bg-gradient-to-br from-purple-100 to-purple-200'
                            }`}>
                              <span className="text-xl">
                                {queued.event_type === 'birthday' ? '🎂' : '💑'}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  queued.event_type === 'birthday' 
                                    ? 'bg-pink-100 text-pink-800 border border-pink-200' 
                                    : 'bg-purple-100 text-purple-800 border border-purple-200'
                                }`}>
                                  {queued.event_type === 'birthday' ? 'Birthday' : 'Anniversary'}
                                </span>
                                <span className="font-bold text-gray-900">{queued.guests?.name || 'Unknown'}</span>
                              </div>
                              <p className="text-sm text-gray-600 font-medium">
                                Event: {formatDate(queued.event_date)}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Scheduled: {formatDate(queued.scheduled_send_date)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                            <span className="text-sm font-semibold text-gray-700">Discount:</span>
                            {editingDiscount === queued.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="5"
                                  max="25"
                                  value={newDiscount}
                                  onChange={(e) => setNewDiscount(e.target.value)}
                                  className="w-24 px-3 py-2 text-sm border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 font-semibold"
                                  placeholder={queued.discount_percentage}
                                />
                                <span className="text-sm font-semibold text-gray-700">%</span>
                                <button
                                  onClick={() => updateDiscount(queued.id, queued.discount_percentage)}
                                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 font-semibold shadow-md transition-all"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingDiscount(null);
                                    setNewDiscount('');
                                  }}
                                  className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-semibold transition-all"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <span className="text-lg font-bold text-gray-900 bg-gray-100 px-4 py-2 rounded-xl">{queued.discount_percentage}%</span>
                                <button
                                  onClick={() => {
                                    setEditingDiscount(queued.id);
                                    setNewDiscount(queued.discount_percentage);
                                  }}
                                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-semibold transition-all"
                                >
                                  Edit
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border border-yellow-200">
                            Queued
                          </span>
                          <button
                            onClick={() => cancelQueued(queued.id)}
                            className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-xl transition-all shadow-sm"
                            title="Cancel"
                          >
                            <FiX size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sent History */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Sent History</h2>
                <p className="text-sm text-gray-500">Previously sent automated messages</p>
              </div>
              
              {loadingHistory ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading history...</p>
                </div>
              ) : sentHistory.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <FiCheckCircle className="text-3xl text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-medium">No messages sent yet</p>
                  <p className="text-xs text-gray-500 mt-1">Sent messages will appear here</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {sentHistory.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`rounded-xl p-2 flex-shrink-0 ${
                          item.event_type === 'birthday' 
                            ? 'bg-gradient-to-br from-pink-100 to-pink-200' 
                            : 'bg-gradient-to-br from-purple-100 to-purple-200'
                        }`}>
                          <span className="text-xl">
                            {item.event_type === 'birthday' ? '🎂' : '💑'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              item.event_type === 'birthday' 
                                ? 'bg-pink-100 text-pink-800 border border-pink-200' 
                                : 'bg-purple-100 text-purple-800 border border-purple-200'
                            }`}>
                              {item.event_type === 'birthday' ? 'Birthday' : 'Anniversary'}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              item.status === 'sent' 
                                ? 'bg-green-100 text-green-800 border border-green-200' 
                                : 'bg-red-100 text-red-800 border border-red-200'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                          <h3 className="font-bold text-gray-900 text-lg mb-1">{item.guests?.name || 'Unknown'}</h3>
                          <p className="text-sm text-gray-600 font-medium mb-2">
                            Sent: {formatDate(item.sent_date)}
                          </p>
                          <div className="bg-gray-100 rounded-lg p-2 mt-2">
                            <p className="text-xs text-gray-600">
                              Code: <span className="font-mono font-bold text-gray-900">{item.discount_code}</span>
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              Discount: <span className="font-bold text-gray-900">{item.discount_percentage}%</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Message Details */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    isStep1Complete 
                      ? 'bg-green-100 border-green-500 text-green-700' 
                      : 'bg-gray-100 border-gray-300 text-gray-600'
                  }`}>
                    {isStep1Complete ? (
                      <FiCheckCircle className="h-4 w-4" />
                    ) : (
                      <span className="font-bold text-xs">1</span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Message Details</h2>
                    <p className="text-sm text-gray-500">Enter your broadcast title and message</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {/* Broadcast Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Broadcast Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                      broadcastType === 'marketing'
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <input
                        type="radio"
                        value="marketing"
                        checked={broadcastType === 'marketing'}
                        onChange={(e) => setBroadcastType(e.target.value)}
                        className="sr-only"
                      />
                      <span className={`text-sm font-medium ${broadcastType === 'marketing' ? 'text-gray-900' : 'text-gray-600'}`}>
                        Marketing/Offer
                      </span>
                    </label>
                    <label className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                      broadcastType === 'event'
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <input
                        type="radio"
                        value="event"
                        checked={broadcastType === 'event'}
                        onChange={(e) => setBroadcastType(e.target.value)}
                        className="sr-only"
                      />
                      <span className={`text-sm font-medium ${broadcastType === 'event' ? 'text-gray-900' : 'text-gray-600'}`}>
                        Event/Announcement
                      </span>
                    </label>
                  </div>
                </div>

                {/* Target Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <select
                    value={targetType}
                    onChange={(e) => {
                      setTargetType(e.target.value);
                      setSelectedGuests([]);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  >
                    <option value="all">All Guests</option>
                    <option value="regular_customers">Regular Customers</option>
                    <option value="phone_based">Select Specific Guests</option>
                  </select>
                </div>

                {/* Guest Selector for Phone Based */}
                {targetType === 'phone_based' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Select Guests
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowGuestSelector(!showGuestSelector)}
                        className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                      >
                        {showGuestSelector ? 'Hide' : 'Show'} Guest List
                      </button>
                    </div>
                    
                    {showGuestSelector && (
                      <div className="max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg p-3">
                        {loadingGuests ? (
                          <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                            <p className="text-gray-500 mt-3 text-sm">Loading guests...</p>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={handleSelectAll}
                              className="mb-3 px-3 py-1.5 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium transition-colors"
                            >
                              {selectedGuests.length === availableGuests.length ? 'Deselect All' : 'Select All'}
                            </button>
                            <div className="space-y-2">
                              {availableGuests.map(guest => (
                                <label key={guest.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer border border-transparent hover:border-gray-200 transition-all">
                                  <input
                                    type="checkbox"
                                    checked={selectedGuests.includes(guest.phone)}
                                    onChange={() => handleGuestToggle(guest.phone)}
                                    className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded cursor-pointer"
                                  />
                                  <span className="text-sm font-medium text-gray-700 flex-1">
                                    {guest.name} {guest.phone && <span className="text-gray-500">({guest.phone})</span>}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-3 bg-gray-100 rounded-lg px-3 py-2">
                      <p className="text-sm font-medium text-gray-900">
                        {selectedGuests.length} guest{selectedGuests.length !== 1 ? 's' : ''} selected
                      </p>
                    </div>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    placeholder="e.g., Special Offer, New Menu"
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm resize-none"
                    placeholder="Enter your message here..."
                    required
                  />
                </div>

                {/* Image Upload */}
                <div className="space-y-3 pt-2 border-t border-gray-100">
                  <label className="block text-sm font-semibold text-gray-700">Add Image (Required)</label>
                  
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <div className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg transition-all ${
                          'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                        }`}>
                          <FiUpload className="h-5 w-5 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Upload from Computer</span>
                        </div>
                      </label>
                      <input
                        id="image-upload"
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </div>

                    <div>
                      <input
                        type="text"
                        value={imageUrl}
                        onChange={(e) => {
                          setImageUrl(e.target.value);
                          if (/^https?:\/\//i.test(e.target.value)) {
                            setImagePreview(e.target.value);
                          } else if (!e.target.value) {
                            setImagePreview(null);
                          }
                        }}
                        placeholder="Paste public HTTPS image URL"
                        className="w-full h-11 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                      />
                    </div>
                  </div>

                  {(imagePreview || imageUrl) && (
                    <div className="relative inline-block mt-2">
                      <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 shadow-sm">
                        <img
                          src={imagePreview || imageUrl}
                          alt="Preview"
                          className="max-h-40 max-w-full object-contain"
                          onError={async () => {
                            setImagePreview(null);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setImageUrl('');
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                          title="Remove image"
                        >
                          <FiXCircle className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    WhatsApp templates require a public HTTPS image URL. Upload is preview-only unless you host the image.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2: Recipients */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    isStep2Complete 
                      ? 'bg-green-100 border-green-500 text-green-700' 
                      : 'bg-gray-100 border-gray-300 text-gray-600'
                  }`}>
                    {isStep2Complete ? (
                      <FiCheckCircle className="h-4 w-4" />
                    ) : (
                      <span className="font-bold text-xs">2</span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Add Recipients</h2>
                    <p className="text-sm text-gray-500">Select target audience for your broadcast</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {targetType !== 'phone_based' && (
                  <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg bg-gray-50">
                    <FiUsers className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">All guests will receive this broadcast</p>
                    <p className="text-xs text-gray-400 mt-1">Select "Select Specific Guests" to choose individual recipients</p>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setTitle('');
                  setMessage('');
                  setImagePreview(null);
                  setImageUrl('');
                  setSelectedGuests([]);
                  setTargetType('all');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
              >
                Clear Form
              </button>
              <button 
                type="submit" 
                disabled={sending || !canSubmit} 
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend className="h-4 w-4" />
                    Send Broadcast
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Broadcast History */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Broadcast History</h3>
            </div>
            <div className="p-6">
              {loadingBroadcasts ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading broadcasts...</p>
                </div>
              ) : broadcasts.length === 0 ? (
                <div className="text-center py-12">
                  <FiUsers className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No broadcasts sent yet</p>
                  <p className="text-xs text-gray-400 mt-1">Create your first broadcast above</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {broadcasts.map((broadcast) => (
                    <div
                      key={broadcast.id}
                      className="flex items-start justify-between p-4 border border-gray-200 rounded-lg bg-white hover:border-gray-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FiSend className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <h3 className="font-semibold text-gray-900 truncate">{broadcast.title}</h3>
                          {broadcast.image_url && (
                            <FiImage className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded flex-shrink-0">
                            {broadcast.broadcast_type || 'general'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {broadcast.message}
                        </p>
                        <div className="flex items-center flex-wrap gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1.5">
                            <FiClock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            {formatDate(broadcast.created_at)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <FiUsers className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            Target: {broadcast.target_count || 0}
                          </span>
                          <span className="flex items-center gap-1.5 text-green-600 font-medium">
                            <FiCheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                            Sent: {broadcast.sent_count || 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold border ${
                            getStatusColor(broadcast.status) || 'bg-gray-100 text-gray-800 border-gray-200'
                          }`}
                        >
                          {broadcast.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default RestaurantBroadcast;
